import { Pencil, Mic, Send } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, Eyebrow } from "@/components/PageHero";
import CTASection from "@/components/CTASection";

const phases = [
  {
    n: "01", title: "Clarify", icon: Pencil,
    head: "Clarify, Know exactly who you are and who you serve.",
    happens: "ICP discovery, brand identity work, competitive positioning, and pinpointing what makes you the obvious choice.",
    walk: "A clear ideal client profile, a positioning statement, and a core message framework.",
  },
  {
    n: "02", title: "Refine", icon: Mic,
    head: "Refine. Your message, in your voice, delivered with confidence.",
    happens: "Audience pain/desire/fear research, ad script writing, on-camera video coaching, and filming support.",
    walk: "Polished video ad creative that sounds and looks like you, not a generic ad.",
  },
  {
    n: "03", title: "Amplify", icon: Send,
    head: "Amplify. Launch and multiply your message across your local market.",
    happens: "Meta campaign build, audience targeting, ad set structure, budget management, ongoing optimization, and monthly reporting.",
    walk: "A running local ad presence that builds awareness, trust, and leads over time.",
  },
];

const timeline = [
  { w: "Week 1-2", t: "Discovery & Clarity Session" },
  { w: "Week 3-4", t: "Script writing & Video Coaching" },
  { w: "Week 5-6", t: "Creative Production & Campaign Build" },
  { w: "Week 7+", t: "Launch, Optimize, Report" },
];

export default function Process() {
  return (
    <>
      <Seo
        title="Our Process | Meta Ads & Brand Voice | Prolific Maven, FL"
        description="A process built around your voice, not a template. Three phases: Clarify, Refine, Amplify. See what the first 90 days with Prolific Maven look like."
        path="/process"
      />
      <PageHero
        eyebrow="The Process"
        title="A process built around your voice. Not a template."
        subtitle="Three phases. One outcome: a local market that knows your name."
      />

      <section className="relative z-10 bg-ink-800 border-t border-white/10" data-testid="process-phases">
        {phases.map((p, i) => (
          <Reveal key={p.n} className="border-b border-white/10">
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28 grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <span className="font-display font-black text-7xl sm:text-8xl lg:text-9xl text-gold leading-none">{p.n}</span>
                <div className="flex items-center gap-3 mt-6">
                  <p.icon className="text-gold" size={26} />
                  <span className="font-display font-bold text-2xl text-white">{p.title}</span>
                </div>
              </div>
              <div className="lg:col-span-8">
                <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white leading-tight">{p.head}</h2>
                <div className="mt-8 grid sm:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-gold mb-3">What happens</h3>
                    <p className="text-cream/70 leading-relaxed">{p.happens}</p>
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-widest text-gold mb-3">What you walk away with</h3>
                    <p className="text-cream/70 leading-relaxed">{p.walk}</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="relative z-10 bg-ink-900 py-24 sm:py-32" data-testid="process-timeline">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal><Eyebrow>The First 90 Days</Eyebrow></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-5 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white">What the first 90 days look like</h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {timeline.map((s, i) => (
              <Reveal key={s.w} delay={i * 100} className="bg-ink-800 p-8">
                <div className="w-3 h-3 bg-gold rounded-full" />
                <p className="mt-6 text-gold text-sm uppercase tracking-wider">{s.w}</p>
                <p className="mt-2 text-white font-display font-semibold text-lg leading-snug">{s.t}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
